import type { IUseDateInput } from '$/components/elements/Inputs/hooks/useDateInput';
import type { UseFormReturn } from 'react-hook-form';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import DayPicker from 'react-day-picker';

import TextInput from '../TextInput';

import theme from '$/assets/theme';
import useDateInput from '$/components/elements/Inputs/hooks/useDateInput';
import { DateTypes } from '$/types/enums/DateTypes';
import dateFormatToGermanPlaceholder from '$/utils/transforms/dateToGermanPlaceholder';

const currentYear = new Date().getFullYear();
const fromMonth = new Date(currentYear - 50, 0);
const toMonth = new Date(currentYear + 20, 11);

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const WEEKDAYS_LONG = [
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
];
const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const YearMonthForm = ({
  date,
  onChange,
}: {
  date: Date;
  onChange: ReturnType<
    ReturnType<typeof useDateInput>['handleDatePickerOnChange']
  >;
}) => {
  const months = MONTHS;

  const years = [];
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i);
  }

  const handleChangeMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = e.target.value;
    onChange(new Date(date.getFullYear(), Number(month)));
  };

  const handleChangeYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value;
    onChange(new Date(Number(year), date.getMonth()));
  };

  return (
    <div className="DayPicker-Caption">
      <select name="month" onChange={handleChangeMonth} value={date.getMonth()}>
        {months.map((month, i) => (
          <option key={month} value={i}>
            {month}
          </option>
        ))}
      </select>
      <select
        name="year"
        onChange={handleChangeYear}
        value={date.getFullYear()}
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
};

export interface IDateInputProps extends Omit<IUseDateInput, 'dateFormat'> {
  name: string;
  onBlur?: () => void;
  iconPosition?: 'end' | 'start';
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  error?: boolean;
  errorMessage?: string | JSX.Element | undefined | null;
  disabled?: boolean;
  setError?: UseFormReturn<any>['setError'];
}

const dateFormat = DateTypes.DAY_MONTH_YEAR_DOT;
const placeholder = dateFormatToGermanPlaceholder(dateFormat);

const DateInput = React.forwardRef(
  (
    { value, onChange, iconPosition, error = false, ...props }: IDateInputProps,
    _
  ) => {
    const {
      dialogRef,
      datePickerValue,
      handleDatePickerOnChange,
      handleTextInputOnChange,
      textInputValue,
      openModal,
      isModalOpen,
    } = useDateInput({ value, onChange, dateFormat, ...props });

    const [dayPickerSelected, setDayPickerSelected] = useState(false);

    return (
      <div ref={dialogRef}>
        <div className={`YearNavigation ${isModalOpen ? 'open' : 'closed'}`}>
          <DayPicker
            locale="de"
            months={MONTHS}
            weekdaysShort={WEEKDAYS_SHORT}
            weekdaysLong={WEEKDAYS_LONG}
            month={datePickerValue ?? undefined}
            fromMonth={fromMonth}
            toMonth={toMonth}
            onDayClick={(e) => {
              setDayPickerSelected(true);
              handleDatePickerOnChange()(e);
            }}
            selectedDays={datePickerValue ?? undefined}
            captionElement={({ date }) => (
              <YearMonthForm
                date={date}
                onChange={handleDatePickerOnChange({
                  closeModalOnChange: false,
                })}
              />
            )}
            modifiersStyles={{ color: 'white' }}
          />
        </div>
        <TextInput
          {...props}
          placeholder={placeholder}
          value={textInputValue}
          onInput={handleTextInputOnChange}
          icon={
            <FontAwesomeIcon
              name={props.name}
              icon={faCalendar}
              onClick={openModal}
              style={{ cursor: 'pointer' }}
            />
          }
          iconPosition={iconPosition}
          ref={dialogRef}
          errorMessage={props.errorMessage}
          error={error}
          onBlur={props.onBlur}
          containerProps={{
            style: {
              borderColor: error ? theme.colors.functionalRed : undefined,
            },
          }}
          inputProps={{
            autoComplete: 'off',
          }}
          datePickerSelected={dayPickerSelected}
          maskedInput
          hideErrorIcon
        />
      </div>
    );
  }
);

DateInput.defaultProps = {
  error: false,
  errorMessage: 'Bitte angeben',
  iconPosition: 'end',
};

export default DateInput;
